export const USER_PAGE_SIZES = [10, 20, 50, 100];

export const USER_ROLES = ["customer", "sales", "admin"];

export const USER_SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name_asc", label: "Name: A–Z" },
  { value: "email_asc", label: "Email: A–Z" },
  { value: "role_asc", label: "Role: A–Z" },
];

export function buildPageItems(current, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items = new Set([1, totalPages, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => items.add(n));
  if (current >= totalPages - 2) {
    [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => items.add(n));
  }
  return [...items]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b)
    .reduce((acc, n, i, arr) => {
      if (i > 0 && n - arr[i - 1] > 1) acc.push("…");
      acc.push(n);
      return acc;
    }, []);
}

export function paginateItems(items, page = 1, pageSize = 10) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = (safePage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);

  return {
    items: items.slice(start, end),
    page: safePage,
    pageSize,
    total,
    totalPages,
    showingFrom: total === 0 ? 0 : start + 1,
    showingTo: end,
  };
}

function getSearchHaystack(user) {
  return [user.name, user.email, user.phone, user.role]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function compareUsers(a, b, sortBy) {
  switch (sortBy) {
    case "oldest":
      return new Date(a.createdAt) - new Date(b.createdAt);
    case "name_asc":
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      });
    case "email_asc":
      return String(a.email || "").localeCompare(String(b.email || ""), undefined, {
        sensitivity: "base",
      });
    case "role_asc":
      return String(a.role || "").localeCompare(String(b.role || ""), undefined, {
        sensitivity: "base",
      });
    case "newest":
    default:
      return new Date(b.createdAt) - new Date(a.createdAt);
  }
}

export function filterAndSortUsers(users, filters) {
  const {
    search = "",
    role = "all",
    status = "all",
    dateFrom = "",
    dateTo = "",
    sortBy = "newest",
  } = filters;

  const query = search.trim().toLowerCase();
  const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
  const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;

  const filtered = (users || []).filter((user) => {
    if (role !== "all" && user.role !== role) return false;

    if (status === "active" && user.isActive === false) return false;
    if (status === "inactive" && user.isActive !== false) return false;

    if (fromTime || toTime) {
      const joined = new Date(user.createdAt).getTime();
      if (fromTime && joined < fromTime) return false;
      if (toTime && joined > toTime) return false;
    }

    if (query && !getSearchHaystack(user).includes(query)) return false;
    return true;
  });

  return [...filtered].sort((a, b) => compareUsers(a, b, sortBy));
}
